import { Check, Edit, LayoutDashboard, Lock, Plus, SlidersHorizontal } from '../../../icons';
import { useMenuEnabledFn } from '../../../lib/menu-gate';
import { BOARD_KIND_LABEL, BOARD_VISIBILITY_LABEL, type DashboardBoard } from '../../../store/dashboardStore';
import { getWidgetDef } from './widget-registry';
import {
  STARTER_TEMPLATES, droppedNote, pickStarterWidgets, type CreateBoardPreset,
} from './starter-templates';
import './dashboard-frame-v2.css';

/**
 * 대시보드 틀 v2 (2026-09-16 · 대시보드 시안 v2 V5) — 머리 카드 · 편집 중 띠 · 잠김 띠 · 처음 여는 사람.
 *
 * ★ 깃발(`VITE_DASHBOARD_V2`)이 켜졌을 때만 `DashboardBoard` 가 이것을 그린다. 꺼져 있으면 종전 툴바 그대로.
 * ★★ 탭 막대 순서 바꾸기는 **종전 포인터 끌기 그대로**다(Q-5) — 모양만 `.dwv-frame` 아래 CSS 로 바꾼다.
 * ★ 머리 카드는 보드 스크롤 «안»에 선다(시안) — 위젯과 함께 올라간다.
 */

export function BoardHeadV2({
  board, editing, onToggleEditing, onAddWidget, onSettings,
}: {
  board: DashboardBoard;
  editing: boolean;
  onToggleEditing: () => void;
  onAddWidget: () => void;
  onSettings: () => void;
}) {
  return (
    <section className="dwv-head" aria-label="대시보드 도구">
      <h2 className="dwv-head__title">{board.name}</h2>
      <span className="dwv-head__meta">
        {`${BOARD_KIND_LABEL[board.kind]} 대시보드 · 위젯 ${board.widgets.length}개 · ${BOARD_VISIBILITY_LABEL[board.visibility]}`}
      </span>
      <div className="dwv-head__end">
        <button type="button" className={`dw-btn${editing ? ' dw-btn--primary' : ''}`} onClick={onToggleEditing}>
          {editing ? <Check size={14} aria-hidden /> : <Edit size={14} aria-hidden />}
          {editing ? '편집 완료' : '레이아웃 편집'}
        </button>
        <button type="button" className="dw-btn" onClick={onAddWidget}>
          <Plus size={14} aria-hidden />위젯 추가
        </button>
        <button type="button" className="dw-btn" onClick={onSettings}>
          <SlidersHorizontal size={14} aria-hidden />설정
        </button>
      </div>
    </section>
  );
}

/** 편집 중 띠 — 무엇을 끌 수 있고 누구에게 적용되는지. 켜지는 순간 읽어 준다(`role=status`). */
export function EditBandV2() {
  return (
    <div className="dwv-band dwv-band--edit" role="status">
      <b>편집 중</b>
      <span>손잡이로 옮기고, 오른쪽 모서리를 끌어 폭(1/3 · 1/2 · 2/3 · 전체)을 바꿉니다 · 배치는 나에게만 적용됩니다</span>
    </div>
  );
}

/**
 * 잠김 띠 — 전사처럼 회사가 관리하는 대시보드.
 * ★ 「나의 대시보드로」는 **갈 곳이 있을 때만** 그린다. 눌러도 아무 일이 없는 단추는 두지 않는다.
 */
export function LockedBandV2({ onGoMine, onOpenWidgetAdmin }: {
  onGoMine?: () => void;
  /**
   * 「통합설정 › 위젯 관리」로 가기 — **관리자일 때만** 넘긴다(2026-09-16 · #1407 뺀 줄 복원).
   * ★ 경로 글자는 누구에게나 보인다(누구에게 물어야 하는지 알게). 누를 수 있는 것은 그 판을 여는 사람뿐이다 —
   *   직원이 누르면 `/settings` 는 「내 프로필로」 안내만 보여 준다(Q-7 「결재선 관리」 링크와 같은 원칙).
   */
  onOpenWidgetAdmin?: () => void;
}) {
  return (
    <div className="dwv-band">
      <Lock size={14} aria-hidden />
      <b>회사 담당자가 관리합니다</b>
      <span>이 대시보드는 편집 · 이동 · 위젯 추가가 잠겨 있습니다</span>
      {onOpenWidgetAdmin
        ? <button type="button" className="dwv-band__link" onClick={onOpenWidgetAdmin}>통합설정 › 위젯 관리</button>
        : <span className="dwv-band__path">통합설정 › 위젯 관리</span>}
      {onGoMine && (
        <span className="dwv-band__end">
          <button type="button" className="dw-btn dw-btn--xs" onClick={onGoMine}>나의 대시보드로</button>
        </span>
      )}
    </div>
  );
}

/**
 * 「나의 대시보드로」가 갈 곳 — 잠기지 않고 숨기지 않은 것 중 기본 → 개인 → 첫 번째.
 * 없으면 null(단추를 그리지 않는다).
 */
export function myBoardTarget(
  boards: readonly DashboardBoard[], hiddenIds: readonly string[], defaultId: string | null,
): DashboardBoard | null {
  const open = boards.filter((b) => !b.locked && !hiddenIds.includes(b.id));
  return open.find((b) => b.id === defaultId) ?? open.find((b) => b.kind === 'personal') ?? open[0] ?? null;
}

/**
 * 처음 여는 사람 — 빈 카드 + 시작 틀 셋 (규칙 D12).
 *
 * ★ 틀을 눌러도 **바로 만들지 않는다** — 새 대시보드 창을 그 틀로 채워 연다(시안: 틀 → 새 대시보드 창).
 *   이름과 위젯을 한 번 보고 「만들기」를 누르게 한다.
 * ★ 메뉴가 꺼진 위젯은 카드에서부터 빠져 보인다 — 만든 뒤에 사라지면 속은 기분이 든다.
 */
export function FirstVisitV2({
  onCreate, onPickTemplate,
}: {
  onCreate: () => void;
  onPickTemplate: (preset: CreateBoardPreset) => void;
}) {
  const menuEnabled = useMenuEnabledFn();

  return (
    <div className="dwv-first">
      <button type="button" className="dwv-empty" onClick={onCreate}>
        <span className="dwv-empty__ic" aria-hidden><LayoutDashboard size={28} /></span>
        <b>대시보드가 없습니다</b>
        <small>눌러서 만드세요 · 위젯을 골라 담고 원하는 자리로 옮길 수 있습니다</small>
        <span className="dw-btn dw-btn--primary" aria-hidden><Plus size={14} />새 대시보드</span>
      </button>

      <ul className="dwv-tpls" aria-label="시작 틀">
        {STARTER_TEMPLATES.map((t) => {
          const pick = pickStarterWidgets(t, menuEnabled);
          const note = droppedNote(pick.dropped);
          const names = pick.widgets.map((w) => getWidgetDef(w.key)?.label ?? w.key);
          return (
            <li key={t.id}>
              <button
                type="button"
                className="dwv-tpl"
                disabled={pick.widgets.length === 0}
                onClick={() => onPickTemplate({
                  templateId: t.id,
                  name: t.name,
                  widgets: pick.widgets.map(({ key, span }) => ({ key, span })),
                  note,
                })}
              >
                <span className="dwv-mini" aria-hidden>
                  {pick.widgets.map((w) => <i key={w.key} className={`dwv-mini__c dwv-mini__c--${w.span}`} />)}
                </span>
                <b>{t.name}</b>
                <small>{names.length > 0 ? names.join(' · ') : '담을 위젯이 없습니다'}</small>
                {note && <small className="dwv-tpl__note">{note}</small>}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
